import { Injectable } from '@angular/core';

import { CurrencyMeta, CurrencyRegistry } from './shared/currency';
import { setPortalLocale } from './shared/portal-format';

export interface PortalBranding {
	organisation?: { company_name?: string; alias?: string; alias_matched?: string; logo_url?: string | null };
	date_locale?: string;
	currencies?: CurrencyMeta[];
	settings?: {
		primary_colour?: string;
		brand_colours?: string[];
		appearance?: 'light' | 'dark';
		[k: string]: any;
	};
	font?: { font_family?: string; url?: string } | null;
	active_cover?: { url?: string } | null;
	covers?: { id: number; url: string }[];
}

@Injectable({ providedIn: 'root' })
export class PortalBrandingService {

	branding: PortalBranding | null = null;

	get companyName(): string {
		return this.branding?.organisation?.company_name || 'Customer Portal';
	}

	get loginCoverUrl(): string | null {
		return this.branding?.active_cover?.url || null;
	}

	get logoUrl(): string | null {
		return this.branding?.organisation?.logo_url || null;
	}

	apply(branding: PortalBranding | null | undefined): void {
		if (!branding) return;
		this.branding = branding;

		// Apply the org's date locale so all portal dates format like the staff app.
		setPortalLocale(branding.date_locale || 'en-GB');

		// Feed the shared CurrencyRegistry so the `currency` pipe renders amounts
		// with the same affixes and precision as the staff app.
		if (branding.currencies?.length) CurrencyRegistry.set(branding.currencies);

		const root = document.documentElement;
		const settings = branding.settings || {};

		// Accent / CTA colour = org primary_colour.
		const accent = this.normaliseColour(settings.primary_colour) || '#1f2f3e';
		root.style.setProperty('--brand-accent', accent);
		root.style.setProperty('--brand-on-accent', this.contrastColour(accent));

		// Dark mode derives the whole neutral palette (backgrounds, surfaces =
		// button fills, borders, text) from the accent hue at low saturation, so the
		// portal reads as one accent-tinted near-black theme. Light mode keeps the
		// static theme.scss neutrals; its header/hero shade follows the accent.
		if (settings.appearance === 'dark') {
			root.classList.add('portal-dark');
			this.applyDarkNeutrals(root, accent);
		} else {
			root.classList.remove('portal-dark');
			this.clearDerivedNeutrals(root);
			root.style.setProperty('--brand-dark', accent);
			root.style.setProperty('--brand-on-dark', this.contrastColour(accent));
		}

		if (branding.font?.url && branding.font.font_family) {
			this.loadFont(branding.font.url, branding.font.font_family);
			const fam = `'${branding.font.font_family}'`;
			root.style.setProperty('--brand-font', `${fam}, 'Figtree', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`);
			root.style.setProperty('--brand-heading-font', `${fam}, 'Mulish', sans-serif`);
		} else {
			root.style.removeProperty('--brand-font');
			root.style.removeProperty('--brand-heading-font');
		}

		if (branding.organisation?.company_name) {
			document.title = branding.organisation.company_name + ' — Portal';
		}
	}

	// Dark-mode neutrals derived from the accent hue (mirrors the design reference's
	// applyDarkTokens): a low-saturation tint of the accent at fixed lightness steps.
	// Surfaces are the button fills; borders/text follow the same hue.
	private applyDarkNeutrals(root: HTMLElement, accent: string): void {
		const [h, s] = this.hexToHsl(accent);
		const H = (sat: number, l: number) => this.hslToHex(h, sat, l);
		const set = (name: string, val: string) => root.style.setProperty(name, val);

		const brandDark = H(Math.min(s * 0.55, 35), 62);
		set('--brand-bg', H(Math.min(s * 0.25, 12), 10));
		set('--brand-dark', brandDark);
		set('--brand-on-dark', this.contrastColour(brandDark));
		set('--brand-muted', H(Math.min(s * 0.20, 15), 52));
		set('--c-surface', H(Math.min(s * 0.30, 14), 12));
		set('--c-surface-2', H(Math.min(s * 0.25, 12), 15));
		set('--c-hover', H(Math.min(s * 0.25, 12), 18));
		set('--c-border-1', H(Math.min(s * 0.30, 16), 20));
		set('--c-border-2', H(Math.min(s * 0.25, 14), 23));
		set('--c-border-3', H(Math.min(s * 0.22, 12), 26));
		set('--c-separator', H(Math.min(s * 0.25, 12), 16));
		set('--c-text-1', H(Math.min(s * 0.12, 7), 85));
		set('--c-text-2', H(Math.min(s * 0.08, 4), 93));
		set('--c-text-muted', H(Math.min(s * 0.18, 9), 42));
		set('--c-shadow', 'rgba(0, 0, 0, 0.35)');

		const hero = H(Math.min(s * 0.40, 22), 8);
		set('--hero-rgb', `${parseInt(hero.slice(1, 3), 16)}, ${parseInt(hero.slice(3, 5), 16)}, ${parseInt(hero.slice(5, 7), 16)}`);
	}

	// Remove the dark-derived neutrals so the static (light) theme.scss values apply.
	private clearDerivedNeutrals(root: HTMLElement): void {
		['--brand-bg', '--brand-muted', '--c-surface', '--c-surface-2', '--c-hover',
			'--c-border-1', '--c-border-2', '--c-border-3', '--c-separator',
			'--c-text-1', '--c-text-2', '--c-text-muted', '--c-shadow', '--hero-rgb']
			.forEach(v => root.style.removeProperty(v));
	}

	private hexToHsl(hex: string): [number, number, number] {
		let h = hex.replace('#', '');
		if (h.length === 3) h = h.split('').map(c => c + c).join('');
		const r = parseInt(h.substr(0, 2), 16) / 255;
		const g = parseInt(h.substr(2, 2), 16) / 255;
		const b = parseInt(h.substr(4, 2), 16) / 255;
		const max = Math.max(r, g, b), min = Math.min(r, g, b);
		const l = (max + min) / 2;
		let hue = 0, sat = 0;
		if (max !== min) {
			const d = max - min;
			sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
			switch (max) {
				case r: hue = (g - b) / d + (g < b ? 6 : 0); break;
				case g: hue = (b - r) / d + 2; break;
				default: hue = (r - g) / d + 4; break;
			}
			hue /= 6;
		}
		return [hue * 360, sat * 100, l * 100];
	}

	private hslToHex(h: number, s: number, l: number): string {
		h = ((h % 360) + 360) % 360;
		s = Math.max(0, Math.min(100, s)) / 100;
		l = Math.max(0, Math.min(100, l)) / 100;
		const c = (1 - Math.abs(2 * l - 1)) * s;
		const x = c * (1 - Math.abs((h / 60) % 2 - 1));
		const m = l - c / 2;
		let r = 0, g = 0, b = 0;
		if (h < 60) { r = c; g = x; } else if (h < 120) { r = x; g = c; } else if (h < 180) { g = c; b = x; } else if (h < 240) { g = x; b = c; } else if (h < 300) { r = x; b = c; } else { r = c; b = x; }
		const to = (v: number) => ('0' + Math.round((v + m) * 255).toString(16)).slice(-2);
		return '#' + to(r) + to(g) + to(b);
	}

	private normaliseColour(value?: string): string | null {
		if (!value) return null;
		return /^#[0-9a-fA-F]{3,8}$/.test(value.trim()) ? value.trim() : null;
	}

	// Pick black/white text for legibility against the primary colour.
	private contrastColour(hex: string): string {
		let h = hex.replace('#', '');
		if (h.length === 3) h = h.split('').map(c => c + c).join('');
		const r = parseInt(h.substr(0, 2), 16);
		const g = parseInt(h.substr(2, 2), 16);
		const b = parseInt(h.substr(4, 2), 16);
		const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
		return luminance > 0.6 ? '#1f2933' : '#ffffff';
	}

	private loadFont(url: string, family: string): void {
		const id = 'portal-font-' + family.replace(/\s+/g, '-').toLowerCase();
		if (document.getElementById(id)) return;
		const link = document.createElement('link');
		link.id = id;
		link.rel = 'stylesheet';
		link.href = url;
		document.head.appendChild(link);
	}
}
